import { useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog, F0Select, F0SelectItemObject } from '@factorialco/f0-react'
import { Input, Textarea } from '@factorialco/f0-react/dist/experimental'
import { BankAccountSelectorF0Query } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { BankAccountSelectorF0 } from 'modules/banking/components/BankAccountSelectorF0'
import { useCreatePaymentForm } from 'modules/banking/components/CreateBulkPaymentForm/useForm'
import { supportsSepaPayment } from 'modules/banking/lib/paymentHelpers'

type BankAccountOption = NonNullable<
  BankAccountSelectorF0Query['banking']
>['bankAccountsConnection']['edges'][number]['node']

export interface NewPaymentModalF0Props {
  onClose: () => void
  onSuccess: (id: number) => void
  enablePaymentMethodFilter?: boolean
}

type PaymentMethod = 'sepa' | 'transfer'

export const NewPaymentModalF0 = ({
  onClose,
  onSuccess,
  enablePaymentMethodFilter = false,
}: NewPaymentModalF0Props) => {
  const { form: creationForm, loading } = useCreatePaymentForm({ onSuccess })
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccountOption | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('transfer')

  const isValid =
    creationForm.isValid &&
    creationForm.values.label?.trim().length > 0 &&
    !!creationForm.values.bankAccountId

  const handleSelectBankAccount = (bankAccount: BankAccountOption | null) => {
    creationForm.setFieldValue('bankAccountId', bankAccount ? String(bankAccount.id) : '')
    setSelectedBankAccount(bankAccount)
  }

  const handlePaymentMethodChange = (value: PaymentMethod | undefined) => {
    if (!value) return
    setPaymentMethod(value)
    setSelectedBankAccount(null)
    creationForm.setFieldValue('bankAccountId', '')
  }

  const paymentMethodOptions: F0SelectItemObject<PaymentMethod>[] = [
    {
      value: 'sepa',
      label: i18n.t('banking.bulk_payment.forms.fields.payment_method.options.sepa'),
    },
    {
      value: 'transfer',
      label: i18n.t('banking.bulk_payment.forms.fields.payment_method.options.transfer'),
    },
  ]

  const bankAccountFilter = useMemo(
    () =>
      enablePaymentMethodFilter
        ? (account: BankAccountOption) => {
            const services = account.account?.providerConfiguration?.services ?? []
            return paymentMethod === 'sepa'
              ? !supportsSepaPayment(services)
              : supportsSepaPayment(services)
          }
        : undefined,
    [enablePaymentMethodFilter, paymentMethod]
  )

  const handleSubmit = () => {
    track(trackingEvents.accounting.payments.clickContinueBulkPayment)
    creationForm.submitForm()
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      onClose={() => {
        if (loading) return
        onClose()
      }}
      title={i18n.t('banking.bulk_payment.new_bulk_payment_modal.title')}
      description={i18n.t('banking.bulk_payment.new_bulk_payment_modal.description')}
      primaryAction={{
        label: i18n.t('banking.bulk_payment.new_bulk_payment_modal.submit'),
        onClick: handleSubmit,
        disabled: !isValid || loading,
        loading,
      }}
      secondaryAction={{
        label: i18n.t('banking.bulk_payment.new_bulk_payment_modal.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s16" flexDirection="column">
        <Input
          required
          label={i18n.t('banking.bulk_payment.forms.fields.name.label')}
          placeholder={i18n.t('banking.bulk_payment.forms.fields.name.placeholder')}
          value={creationForm.values.label}
          onChange={(value) => creationForm.setFieldValue('label', value)}
          error={creationForm.errors.label}
        />
        {enablePaymentMethodFilter && (
          <F0Select<PaymentMethod>
            label={i18n.t('banking.bulk_payment.forms.fields.payment_method.label')}
            placeholder={i18n.t('banking.bulk_payment.forms.fields.payment_method.placeholder')}
            options={paymentMethodOptions}
            value={paymentMethod}
            onChange={(value: PaymentMethod) => handlePaymentMethodChange(value)}
            required
          />
        )}
        <BankAccountSelectorF0
          key={paymentMethod}
          value={selectedBankAccount}
          onChange={handleSelectBankAccount}
          accountNumberTypes={['iban']}
          providerService="sepa_payment_management"
          currency="EUR"
          required={!selectedBankAccount}
          autoSelectSingle
          filter={bankAccountFilter}
        />
        <Textarea
          label={i18n.t('banking.bulk_payment.forms.fields.opt_description.label')}
          placeholder={i18n.t('banking.bulk_payment.forms.fields.opt_description.placeholder')}
          value={creationForm.values.description}
          onChange={(value) => creationForm.setFieldValue('description', value)}
        />
      </Box>
    </F0Dialog>
  )
}
